'use client';
import { useMemo, useState } from 'react';

import { capitalize } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';

import { FormToggleButtonRadioGroup } from '@ui/uikit/components/common/FormToggleButtonRadioGroup';
import type { TableColumn} from '@ui/uikit/components/common/Table';
import { Table, useClientTable } from '@ui/uikit/components/common/Table';
import { Typography } from '@ui/uikit/components/common/Typography';
import { BnbChain, USDT } from '@ui/uikit/components/icons/poly';

import type {
  TabName} from '../../../../../MagicRafflesPage/ui/MagicRaffleEntries/MagicRaffleEntries';
import {
  options
} from '../../../../../MagicRafflesPage/ui/MagicRaffleEntries/MagicRaffleEntries';
import { usePredictionEntries } from '../../../../api/usePredictionRounds/usePredictionEntries';
import type { PredictionEntry } from '../../../../model/PredictionEntry';
import { ContentWrapper } from './cells/ContentWrapper';
import { MyRewardCell } from './cells/MyRewardCell';
import { PredictionCell } from './cells/PredictionCell';

const columns: TableColumn<PredictionEntry>[] = [
  {
    name: 'roundId',
    label: 'Prediction',
    render: ({ row }) => {
      return (
        <PredictionCell
          icon={
            <span className="flex items-center">
              <BnbChain className="icon-xs" />
              <USDT className="icon-xs -ml-2" />
            </span>
          }
          roundNumber={row.roundId}
          timestamp={row.closeTimestamp}
        />
      );
    },
  },
  {
    name: 'userChoice',
    label: 'My Choice',
    render: ({ row }) => {
      return (
        <ContentWrapper>
          {capitalize(row.userChoice)}
          <Typography variant="caption-s" color="tertiary">
            {row.userBet?.formatted}
          </Typography>
        </ContentWrapper>
      );
    },
  },
  {
    name: 'roundResult',
    label: 'Result',
    render: ({ row }) => {
      const result = row.isClaimed ? (
        capitalize(row.roundResult)
      ) : (
        <Typography variant="caption-s" color="tertiary">
          —
        </Typography>
      );

      return <ContentWrapper>{result}</ContentWrapper>;
    },
  },
  {
    name: 'wonAmount',
    label: 'My Reward',
    render: ({ row }) => (
      <MyRewardCell
        value={row.wonAmount.value}
        formattedValue={row.wonAmount.formatted}
        isClaimed={row.isClaimed}
      />
    ),
    contentPadding: 'small',
  },
];

const DEFAULT_EMPTY_STATE_COMPONENT = (
  <Typography variant="caption-s" color="tertiary" textAlign="center">
    No predictions yet.
    <br />
    Make your first prediction now!
  </Typography>
);

export const PredictionEntries = () => {
  const [activeTab, setActiveTab] = useState<TabName>('all');
  const { data: entries = [], isLoading } = usePredictionEntries();
  const isLargeScreen = useMediaQuery('(min-width: 1440px)');

  const filteredRounds = useMemo(() => {
    return activeTab === 'all' ? entries : entries.filter((entry) => entry.wonAmount.value > 0);
  }, [activeTab, entries]);

  const gridTemplateColumns = useMemo(() => {
    return isLargeScreen ? '464px 174px 120px 154px' : '420px 152px 100px 152px';
  }, [isLargeScreen]);

  const onClickTabHandler = (tab: TabName) => {
    setActiveTab(tab);
  };

  const tableProps = useClientTable({
    columns,
    data: filteredRounds,
    isLoading,
    emptyStateComponent: DEFAULT_EMPTY_STATE_COMPONENT,
    gridTemplateColumns,
  });

  return (
    <>
      <div className="flex flex-col gap-4 md-msq:flex-row  md-msq:justify-between md-msq:gap-0 ">
        <Typography variant="h2">Prediction Entries</Typography>
        <FormToggleButtonRadioGroup
          wrapperClassName="max-sm-msq:w-full"
          className="w-full md:w-auto bg-base-bg-secondary text-base-text-tertiary rounded-lg"
          options={options}
          value={activeTab}
          onValueChange={onClickTabHandler}
        />
      </div>
      <Table key={activeTab} {...tableProps} />
    </>
  );
};
